import React, { Fragment, useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import { SITENAMEALIAS, WEBSERVICE } from "../utils/init";
import { getAuthToken, getAuthUuid } from "../utils/service";

// ─────────────────────────────────────────────────────────────────
// Style tokens
// ─────────────────────────────────────────────────────────────────
const CARD = {
  background: "var(--body-color-less)",
  border: "1px solid var(--body-color-less2)",
  borderRadius: "12px",
  padding: "20px 22px",
  marginBottom: "16px",
};

// ─────────────────────────────────────────────────────────────────
// API Service Functions
// ─────────────────────────────────────────────────────────────────
const API_BASE_URL = WEBSERVICE;

const getHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: "bearer " + getAuthToken(),
  UUID: getAuthUuid(),
});

const apiFetch = async (path) => {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: "GET",
    headers: getHeaders(),
  });
  return response.json();
};

const getRewardsAnalytics = (mid, date) =>
  apiFetch(`/campaigns/rewards/analytics?merchantId=${mid}&date=${date}`);
const getTopCampaigns = (mid, date) =>
  apiFetch(`/campaigns/rewards/top-campaigns?merchantId=${mid}&date=${date}`);
const getRecentRewards = (mid, date) =>
  apiFetch(`/campaigns/rewards/recent-rewards?merchantId=${mid}&date=${date}`);
const getBreakdownByType = (mid, date) =>
  apiFetch(`/campaigns/rewards/breakdown?merchantId=${mid}&date=${date}`);
const getMonthlyReport = (mid, date) =>
  apiFetch(`/campaigns/rewards/monthly-report?merchantId=${mid}&date=${date}`);
const getCampaignDetails = (mid, date) =>
  apiFetch(
    `/campaigns/rewards/campaign-details?merchantId=${mid}&date=${date}`
  );

// ─────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────
function Toast({ msg, type }) {
  if (!msg) return null;
  return (
    <div
      style={{
        position: "fixed",
        bottom: "24px",
        right: "24px",
        background: type === "success" ? "#10b981" : "#ef4444",
        color: "#fff",
        padding: "12px 20px",
        borderRadius: "12px",
        fontSize: "13px",
        fontWeight: "600",
        boxShadow: "0 4px 24px rgba(0,0,0,0.25)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        gap: "8px",
        animation: "slideUp .2s ease",
      }}
    >
      <i
        className={`mdi ${
          type === "success" ? "mdi-check-circle" : "mdi-alert-circle"
        }`}
      />
      {msg}
    </div>
  );
}

function Shimmer({ height = 60 }) {
  return (
    <div
      style={{
        height,
        borderRadius: 8,
        background: "var(--body-color-less2)",
        animation: "shimmer 1.4s ease infinite",
      }}
    />
  );
}

function EmptyState({ message }) {
  return (
    <div
      style={{
        textAlign: "center",
        padding: "32px 16px",
        fontSize: 13,
        color: "var(--text-color)",
        opacity: 0.35,
      }}
    >
      {message}
    </div>
  );
}

function StatCard({ icon, color, label, value, sub, loading }) {
  const formatValue = () => {
    if (loading) return "—";
    if (typeof value === "number") {
      if (
        label.toLowerCase().includes("issued") ||
        label.toLowerCase().includes("avg")
      )
        return `$${value.toFixed(2)}`;
      if (label.toLowerCase().includes("rate")) return `${value.toFixed(2)}%`;
      return Math.round(value).toLocaleString();
    }
    return value ?? "—";
  };

  return (
    <div
      className="ra-stat-card"
      style={{
        flex: "1 1 calc(33.333% - 10px)",
        minWidth: 150,
        background: "var(--body-color-less)",
        border: "1px solid var(--body-color-less2)",
        borderRadius: 12,
        padding: "18px 20px",
      }}
    >
      {loading ? (
        <Shimmer height={60} />
      ) : (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 10,
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                flexShrink: 0,
                background: `color-mix(in srgb,${color} 14%,transparent)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 16,
                color,
              }}
            >
              <i className={`mdi ${icon}`} />
            </div>
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: ".6px",
                color: "var(--text-color)",
                opacity: 0.4,
              }}
            >
              {label}
            </span>
          </div>
          <div
            style={{
              fontSize: 26,
              fontWeight: 800,
              color: "var(--text-color)",
              letterSpacing: "-.5px",
              lineHeight: 1,
            }}
          >
            {formatValue()}
          </div>
          {sub && (
            <div
              style={{
                fontSize: 11,
                color: "var(--text-color)",
                opacity: 0.4,
                marginTop: 5,
              }}
            >
              {sub}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function BarChart({ data }) {
  if (!data || data.length === 0)
    return <EmptyState message="No chart data available for this period" />;

  const max = Math.max(...data.map((d) => d.value), 0.01);
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-end",
        gap: 8,
        height: 120,
        padding: "0 4px",
      }}
    >
      {data.map((d, i) => {
        const pct = (d.value / max) * 100;
        const isLast = i === data.length - 1;
        return (
          <div
            key={i}
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 6,
              height: "100%",
              justifyContent: "flex-end",
            }}
          >
            <div
              style={{
                fontSize: 10,
                color: "var(--text-color)",
                opacity: isLast ? 1 : 0.4,
                fontWeight: isLast ? 700 : 400,
              }}
            >
              ${d.value.toFixed(2)}
            </div>
            <div
              style={{
                width: "100%",
                borderRadius: "4px 4px 0 0",
                height: `${Math.max(pct, 2)}%`,
                background: isLast
                  ? "var(--btn-color)"
                  : "color-mix(in srgb,var(--btn-color) 35%,transparent)",
                transition: "height .4s ease",
                boxShadow: isLast
                  ? "0 0 10px color-mix(in srgb,var(--btn-color) 40%,transparent)"
                  : "none",
                minHeight: 4,
              }}
            />
            <div
              style={{
                fontSize: 10,
                color: "var(--text-color)",
                opacity: isLast ? 0.8 : 0.3,
                fontWeight: isLast ? 600 : 400,
              }}
            >
              {d.month}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ProgressBar({ label, value, max, color, subLabel }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div style={{ marginBottom: 14 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 6,
        }}
      >
        <span
          style={{
            fontSize: 13,
            color: "var(--text-color)",
            opacity: 0.75,
            fontWeight: 500,
          }}
        >
          {label}
        </span>
        <span style={{ fontSize: 12, fontWeight: 700, color }}>
          ${value.toFixed(2)}{" "}
          {subLabel && (
            <span style={{ fontSize: 10, opacity: 0.6 }}>{subLabel}</span>
          )}
        </span>
      </div>
      <div
        style={{
          height: 6,
          borderRadius: 3,
          background: "var(--body-color-less2)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            borderRadius: 3,
            width: `${pct}%`,
            background: color,
            transition: "width .6s ease",
            boxShadow: `0 0 8px ${color}60`,
            minWidth: pct > 0 ? 4 : 0,
          }}
        />
      </div>
    </div>
  );
}

// Badge for campaign status
function StatusBadge({ status }) {
  const active = status === "active";
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 600,
        marginLeft: 6,
        color: active ? "#10b981" : "#f59e0b",
      }}
    >
      ● {active ? "Active" : status}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────
export default function RewardsAnalytics() {
  const history = useHistory();

  const [stats, setStats] = useState(null);
  const [topCampaigns, setTopCampaigns] = useState([]);
  const [recentRewards, setRecentRewards] = useState([]);
  const [breakdownData, setBreakdownData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [campaignDetails, setCampaignDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("THIS_MONTH");
  const [toast, setToast] = useState({ msg: "", type: "success" });

  const getMerchantId = () =>
    localStorage.getItem(SITENAMEALIAS + "_merchant_id") || "";

  const showToast = (msg, type = "success", duration = 3000) => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: "", type: "success" }), duration);
  };

  const fetchAllAnalytics = async () => {
    const merchantId = getMerchantId();
    if (!merchantId) {
      showToast("Merchant ID not found", "error", 5000);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const [
        analyticsRes,
        topCampaignsRes,
        recentRewardsRes,
        breakdownRes,
        monthlyReportRes,
        campaignDetailsRes,
      ] = await Promise.all([
        getRewardsAnalytics(merchantId, period),
        getTopCampaigns(merchantId, period),
        getRecentRewards(merchantId, period),
        getBreakdownByType(merchantId, period),
        getMonthlyReport(merchantId, period),
        getCampaignDetails(merchantId, period),
      ]);

      // ── Analytics stats ──
      if (analyticsRes?.status && analyticsRes?.data) {
        const d = analyticsRes.data;
        setStats({
          issuedToday: d.rewards_issued_today ?? 0,
          issuedMonth: d.rewards_issued_month ?? 0,
          customersTotal: d.customers_rewarded ?? 0,
          avgReward: d.avg_reward ?? 0,
          redeemRate: d.redemption_rate ?? 0,
          activeCampaigns: d.active_campaigns ?? 0,
        });
      } else {
        setStats({
          issuedToday: 0,
          issuedMonth: 0,
          customersTotal: 0,
          avgReward: 0,
          redeemRate: 0,
          activeCampaigns: 0,
        });
      }

      // ── Top Campaigns — render ALL, including zero-amount ──
      if (
        topCampaignsRes?.status &&
        Array.isArray(topCampaignsRes?.data?.data)
      ) {
        setTopCampaigns(
          topCampaignsRes.data.data.map((c) => ({
            id: c.campaignName,
            name: c.campaignName,
            type: c.rewardType,
            issued: c.totalRewardAmount ?? 0,
            customers: c.customerCount ?? 0,
            transactions: c.totalTransactions ?? 0,
          }))
        );
      } else {
        setTopCampaigns([]);
      }

      // ── Recent Rewards ──
      if (
        recentRewardsRes?.status &&
        Array.isArray(recentRewardsRes?.data?.data)
      ) {
        setRecentRewards(
          recentRewardsRes.data.data.map((r) => ({
            date: r.dateDisplay,
            amount: r.amount ?? 0,
            customer: r.userId ? `User ${r.userId}` : "—",
            type: r.rewardType,
            campaign: r.campaignName,
          }))
        );
      } else {
        setRecentRewards([]);
      }

      // ── Breakdown by type ──
      if (breakdownRes?.status && Array.isArray(breakdownRes?.data?.data)) {
        setBreakdownData(
          breakdownRes.data.data.map((b) => ({
            label: b.rewardType === "cashback" ? "Cashback" : "Store Credit",
            value: b.totalAmount ?? 0,
            percentage: b.percentageDistribution ?? 0,
            color: b.rewardType === "cashback" ? "#818cf8" : "#2dd4bf",
          }))
        );
      } else {
        setBreakdownData([]);
      }

      // ── Monthly Report / Chart ──
      if (
        monthlyReportRes?.status &&
        Array.isArray(monthlyReportRes?.data) &&
        monthlyReportRes.data.length > 0
      ) {
        setChartData(
          monthlyReportRes.data.map((item) => ({
            month: item.displayName,
            value: item.totalIssued ?? 0,
          }))
        );
      } else {
        setChartData([]);
      }

      // ── Campaign Details — render ALL, including zero-activity ──
      if (
        campaignDetailsRes?.status &&
        Array.isArray(campaignDetailsRes?.data?.data)
      ) {
        setCampaignDetails(
          campaignDetailsRes.data.data.map((c) => ({
            id: c.campaignName,
            name: c.campaignName,
            type: c.rewardType,
            issued: c.totalAmount ?? 0,
            customers: c.customerCount ?? 0,
            transactions: c.transactionCount ?? 0,
            status: c.campaignStatus,
          }))
        );
      } else {
        setCampaignDetails([]);
      }

      showToast("Analytics loaded", "success");
    } catch (error) {
      console.error("Error fetching analytics:", error);
      showToast("Failed to load analytics data", "error", 5000);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllAnalytics();
  }, [period]);

  // Derived values
  const maxIssued =
    topCampaigns.length > 0
      ? Math.max(...topCampaigns.map((c) => c.issued), 0.01)
      : 1;

  const totalIssued =
    breakdownData.length > 0
      ? breakdownData.reduce((sum, b) => sum + b.value, 0)
      : stats?.issuedMonth ?? 0;

  const statCards = [
    {
      icon: "mdi-cash-multiple",
      color: "#10b981",
      label: "Issued Today",
      value: stats?.issuedToday,
      sub: "Rewards paid out today",
    },
    {
      icon: "mdi-calendar-month",
      color: "var(--btn-color)",
      label: "Issued This Month",
      value: stats?.issuedMonth,
      sub: "Current billing period",
    },
    {
      icon: "mdi-account-group",
      color: "#8b5cf6",
      label: "Customers Rewarded",
      value: stats?.customersTotal,
      sub: "Unique customers",
    },
    {
      icon: "mdi-trending-up",
      color: "#f59e0b",
      label: "Avg Reward",
      value: stats?.avgReward,
      sub: "Per transaction",
    },
    {
      icon: "mdi-ticket-confirmation",
      color: "#ec4899",
      label: "Redemption Rate",
      value: stats?.redeemRate,
      sub: "Rewards used at checkout",
    },
    {
      icon: "mdi-trending-up",
      color: "#22c55e",
      label: "Active Campaigns",
      value: stats?.activeCampaigns,
      sub: "Running right now",
    },
  ];

  return (
    <Fragment>
      <style>{`
        @keyframes slideUp  { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        @keyframes shimmer  { 0%{opacity:.4} 50%{opacity:.8} 100%{opacity:.4} }
        .ra-grid > * { min-width: 0; }
        @media(max-width:860px)  { .ra-grid { grid-template-columns: 1fr !important; } }
        @media(max-width:768px)  { .ra-stat-card { flex: 1 1 calc(50% - 7px) !important; min-width: calc(50% - 7px) !important; } }
        @media(max-width:480px)  { .ra-stat-card { flex: 1 1 100% !important; min-width: 100% !important; } }
      `}</style>

      <div className="container-scroller">
        <Sidebar />
        <Header />
        <div className="main-panel">
          <div style={{ padding: "32px 24px 60px" }}>
            <Toast msg={toast.msg} type={toast.type} />

            {/* ── Page Header ── */}
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 12,
                marginBottom: 24,
              }}
            >
              <div>
                <h2
                  style={{
                    fontSize: "22px",
                    fontWeight: "700",
                    color: "var(--text-color)",
                    margin: "0 0 4px",
                    letterSpacing: "-.3px",
                  }}
                >
                  Rewards Analytics
                </h2>
                <p
                  style={{
                    fontSize: 13,
                    color: "var(--text-color)",
                    opacity: 0.45,
                    margin: 0,
                  }}
                >
                  Track the performance of your reward campaigns
                </p>
              </div>

              {/* Period toggle */}
              <div
                style={{
                  display: "flex",
                  background: "var(--body-color)",
                  borderRadius: 9,
                  padding: 3,
                  border: "1px solid var(--body-color-less2)",
                  gap: 3,
                }}
              >
                {[
                  { key: "TODAY", label: "Today" },
                  { key: "THIS_MONTH", label: "This Month" },
                  { key: "ALL_TIME", label: "All Time" },
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => setPeriod(key)}
                    style={{
                      padding: "6px 14px",
                      borderRadius: 7,
                      border: "none",
                      background:
                        period === key ? "var(--btn-color)" : "transparent",
                      color: period === key ? "#fff" : "var(--text-color)",
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                      opacity: period === key ? 1 : 0.5,
                      transition: "all .15s",
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* ── Stat Cards ── */}
            <div
              style={{
                display: "flex",
                gap: 14,
                flexWrap: "wrap",
                marginBottom: 20,
              }}
            >
              {statCards.map((s) => (
                <StatCard key={s.label} {...s} loading={loading} />
              ))}
            </div>

            {/* ── Main Grid ── */}
            <div
              className="ra-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0,58fr) minmax(0,42fr)",
                gap: 20,
                alignItems: "start",
              }}
            >
              {/* ── LEFT COLUMN ── */}
              <div>
                {/* Bar Chart */}
                <div style={CARD}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: 18,
                    }}
                  >
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <i
                        className="mdi mdi-chart-bar"
                        style={{ fontSize: 14, color: "var(--btn-color)" }}
                      />
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: "var(--text-color)",
                          opacity: 0.7,
                        }}
                      >
                        Rewards Issued
                      </span>
                    </div>
                    <span
                      style={{
                        fontSize: 20,
                        fontWeight: 800,
                        color: "var(--text-color)",
                        letterSpacing: "-.5px",
                      }}
                    >
                      {loading ? "—" : `$${totalIssued.toFixed(2)}`}
                    </span>
                  </div>
                  {loading ? (
                    <Shimmer height={120} />
                  ) : (
                    <BarChart data={chartData} />
                  )}
                </div>

                {/* Top Campaigns */}
                <div style={CARD}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: 16,
                    }}
                  >
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <i
                        className="mdi mdi-trophy"
                        style={{ fontSize: 14, color: "#f59e0b" }}
                      />
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: "var(--text-color)",
                          opacity: 0.7,
                        }}
                      >
                        Top Campaigns
                      </span>
                    </div>
                    <button
                      onClick={() => history.push("/rewards/campaigns")}
                      style={{
                        fontSize: 12,
                        color: "var(--btn-color)",
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        fontWeight: 600,
                      }}
                    >
                      View all →
                    </button>
                  </div>

                  {loading ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 10,
                      }}
                    >
                      {[1, 2, 3].map((i) => (
                        <Shimmer key={i} height={52} />
                      ))}
                    </div>
                  ) : topCampaigns.length === 0 ? (
                    <EmptyState message="No campaigns found for this period" />
                  ) : (
                    topCampaigns
                      .slice(0, 5)
                      .map((c, idx) => (
                        <ProgressBar
                          key={c.id + idx}
                          label={c.name}
                          value={c.issued}
                          max={maxIssued}
                          subLabel={`· ${c.customers} customers · ${c.transactions} txns`}
                          color={c.type === "cashback" ? "#818cf8" : "#2dd4bf"}
                        />
                      ))
                  )}
                </div>

                {/* Recent Rewards */}
                <div style={CARD}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 16,
                    }}
                  >
                    <i
                      className="mdi mdi-history"
                      style={{ fontSize: 14, color: "#8b5cf6" }}
                    />
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "var(--text-color)",
                        opacity: 0.7,
                      }}
                    >
                      Recent Rewards Issued
                    </span>
                  </div>

                  {loading ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                      }}
                    >
                      {[1, 2, 3].map((i) => (
                        <Shimmer key={i} height={46} />
                      ))}
                    </div>
                  ) : recentRewards.length === 0 ? (
                    <EmptyState message="No recent rewards for this period" />
                  ) : (
                    recentRewards.slice(0, 10).map((item, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          padding: "10px 0",
                          borderBottom:
                            idx < Math.min(recentRewards.length, 10) - 1
                              ? "1px solid var(--body-color-less2)"
                              : "none",
                        }}
                      >
                        <div
                          style={{
                            width: 36,
                            height: 36,
                            borderRadius: 9,
                            flexShrink: 0,
                            background:
                              item.type === "cashback"
                                ? "rgba(99,102,241,.12)"
                                : "rgba(20,184,166,.12)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 16,
                            color:
                              item.type === "cashback" ? "#818cf8" : "#2dd4bf",
                          }}
                        >
                          <i
                            className={`mdi ${
                              item.type === "cashback"
                                ? "mdi-cash-refund"
                                : "mdi-ticket-percent"
                            }`}
                          />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: "var(--text-color)",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.customer}
                          </div>
                          <div
                            style={{
                              fontSize: 11,
                              color: "var(--text-color)",
                              opacity: 0.45,
                            }}
                          >
                            {item.campaign}
                          </div>
                        </div>
                        <div style={{ textAlign: "right", flexShrink: 0 }}>
                          <div
                            style={{
                              fontSize: 14,
                              fontWeight: 700,
                              color:
                                item.type === "cashback"
                                  ? "#818cf8"
                                  : "#2dd4bf",
                            }}
                          >
                            +${item.amount.toFixed(2)}
                          </div>
                          <div
                            style={{
                              fontSize: 11,
                              color: "var(--text-color)",
                              opacity: 0.35,
                            }}
                          >
                            {item.date}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* ── RIGHT COLUMN ── */}
              <div
                style={{
                  position: "sticky",
                  top: 24,
                  display: "flex",
                  flexDirection: "column",
                  gap: 16,
                }}
              >
                {/* Breakdown by Type */}
                <div style={CARD}>
                  <div
                    style={{
                      fontSize: "10px",
                      fontWeight: "700",
                      textTransform: "uppercase",
                      letterSpacing: ".7px",
                      color: "var(--text-color)",
                      opacity: 0.4,
                      marginBottom: 16,
                    }}
                  >
                    Breakdown by Type
                  </div>

                  {loading ? (
                    <Shimmer height={80} />
                  ) : breakdownData.length === 0 ? (
                    <EmptyState message="No breakdown data for this period" />
                  ) : (
                    <>
                      {breakdownData.map(
                        ({ label, value, percentage, color }) => (
                          <div key={label} style={{ marginBottom: 14 }}>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                marginBottom: 5,
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 7,
                                }}
                              >
                                <span
                                  style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    background: color,
                                    display: "inline-block",
                                    boxShadow: `0 0 6px ${color}80`,
                                  }}
                                />
                                <span
                                  style={{
                                    fontSize: 13,
                                    color: "var(--text-color)",
                                    opacity: 0.7,
                                  }}
                                >
                                  {label}
                                </span>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  gap: 8,
                                  alignItems: "center",
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: 13,
                                    fontWeight: 700,
                                    color,
                                  }}
                                >
                                  ${value.toFixed(2)}
                                </span>
                                <span
                                  style={{
                                    fontSize: 11,
                                    color: "var(--text-color)",
                                    opacity: 0.35,
                                    minWidth: 32,
                                    textAlign: "right",
                                  }}
                                >
                                  {percentage.toFixed(1)}%
                                </span>
                              </div>
                            </div>
                            <div
                              style={{
                                height: 6,
                                borderRadius: 3,
                                background: "var(--body-color-less2)",
                                overflow: "hidden",
                              }}
                            >
                              <div
                                style={{
                                  height: "100%",
                                  borderRadius: 3,
                                  width: `${percentage}%`,
                                  background: color,
                                  boxShadow: `0 0 8px ${color}60`,
                                  transition: "width .6s ease",
                                }}
                              />
                            </div>
                          </div>
                        )
                      )}
                      <div
                        style={{
                          marginTop: 4,
                          paddingTop: 12,
                          borderTop: "1px solid var(--body-color-less2)",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span
                          style={{
                            fontSize: 12,
                            color: "var(--text-color)",
                            opacity: 0.4,
                          }}
                        >
                          Total Issued
                        </span>
                        <span
                          style={{
                            fontSize: 16,
                            fontWeight: 800,
                            color: "var(--text-color)",
                            letterSpacing: "-.3px",
                          }}
                        >
                          ${totalIssued.toFixed(2)}
                        </span>
                      </div>
                    </>
                  )}
                </div>

                {/* Campaign Details */}
                <div style={CARD}>
                  <div
                    style={{
                      fontSize: "10px",
                      fontWeight: "700",
                      textTransform: "uppercase",
                      letterSpacing: ".7px",
                      color: "var(--text-color)",
                      opacity: 0.4,
                      marginBottom: 14,
                    }}
                  >
                    Campaign Details
                  </div>

                  {loading ? (
                    <Shimmer height={120} />
                  ) : campaignDetails.length === 0 ? (
                    <EmptyState message="No campaign details for this period" />
                  ) : (
                    campaignDetails.slice(0, 5).map((c, idx) => (
                      <div
                        key={c.id + idx}
                        onClick={() => history.push("/rewards/campaigns")}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          padding: "10px 0",
                          cursor: "pointer",
                          borderBottom:
                            idx < Math.min(campaignDetails.length, 5) - 1
                              ? "1px solid var(--body-color-less2)"
                              : "none",
                        }}
                      >
                        <div
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: 8,
                            flexShrink: 0,
                            background:
                              c.type === "cashback"
                                ? "rgba(99,102,241,.12)"
                                : "rgba(20,184,166,.12)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 15,
                            color:
                              c.type === "cashback" ? "#818cf8" : "#2dd4bf",
                          }}
                        >
                          <i
                            className={`mdi ${
                              c.type === "cashback"
                                ? "mdi-cash-refund"
                                : "mdi-ticket-percent"
                            }`}
                          />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: "var(--text-color)",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {c.name}
                          </div>
                          <div
                            style={{
                              fontSize: 11,
                              color: "var(--text-color)",
                              opacity: 0.4,
                            }}
                          >
                            {c.transactions} txns · {c.customers} customers
                            <StatusBadge status={c.status} />
                          </div>
                        </div>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: "var(--text-color)",
                            flexShrink: 0,
                          }}
                        >
                          ${c.issued.toFixed(2)}
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Navigation CTAs */}
                <div style={CARD}>
                  {[
                    {
                      icon: "mdi-plus-circle",
                      label: "Create Campaign",
                      path: "/rewards/create",
                      color: "var(--btn-color)",
                    },
                    {
                      icon: "mdi-format-list-bulleted",
                      label: "Manage Campaigns",
                      path: "/rewards/campaigns",
                      color: "#8b5cf6",
                    },
                    {
                      icon: "mdi-home",
                      label: "Rewards Home",
                      path: "/rewards",
                      color: "#10b981",
                    },
                  ].map(({ icon, label, path, color }, idx, arr) => (
                    <div
                      key={path}
                      onClick={() => history.push(path)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        cursor: "pointer",
                        padding: "9px 0",
                        borderBottom:
                          idx < arr.length - 1
                            ? "1px solid var(--body-color-less2)"
                            : "none",
                      }}
                    >
                      <i
                        className={`mdi ${icon}`}
                        style={{ fontSize: 16, color, opacity: 0.8 }}
                      />
                      <span
                        style={{
                          fontSize: 13,
                          color: "var(--text-color)",
                          opacity: 0.7,
                          flex: 1,
                        }}
                      >
                        {label}
                      </span>
                      <i
                        className="mdi mdi-chevron-right"
                        style={{
                          fontSize: 15,
                          color: "var(--text-color)",
                          opacity: 0.2,
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </div>
    </Fragment>
  );
}
